'use client';

import { useRouter } from 'next/navigation';
import { ReviewMushroomUnit } from '@repo/svgs';
import { ResponsiveModal } from '@/components/ui/ResponsiveModal';
import { Button } from '@/components/ui';
import { useAnalytics } from '@/hooks/useAnalytics';
import { usePromptContext } from '@/providers/PromptProvider';
import type { ReportSurface } from '@/lib/prompts/storage';

interface ReportPromptProps {
  open: boolean;
}

export function ReportPrompt({ open }: ReportPromptProps) {
  const router = useRouter();
  const analytics = useAnalytics();
  const { activeReportScreen, dismissActivePrompt, markPromptShown, triggerReportReturnUrl } =
    usePromptContext();

  // Which surface fired this prompt (2nd unit view or 1st building
  // view). Falls back to 'unit' if untracked — shouldn't happen, as
  // schedulePrompt always sets it alongside activePrompt='report'.
  const screen: ReportSurface = activeReportScreen ?? 'unit';

  const handleUnlock = () => {
    analytics.track('report_prompt_unlock_insights', { screen });
    markPromptShown('report', screen);

    // /subscription?returnTo=<encoded>&source=building-report — the
    // subscription page threads `returnTo` through Stripe so the
    // success page lands the user back on the triggering building's
    // report. `source` tags the prompt as the entry point for
    // downstream analytics / UX branching.
    const params = new URLSearchParams();
    if (triggerReportReturnUrl) {
      params.set('returnTo', triggerReportReturnUrl);
    }
    params.set('source', 'building-report');
    // Close the modal before navigating — otherwise the overlay
    // stays mounted through the route transition and briefly
    // renders on top of /subscription. `markPromptShown` already
    // flipped the flag so re-render won't reopen it.
    dismissActivePrompt();
    router.push(`/subscription?${params.toString()}`);
  };

  return (
    <ResponsiveModal
      open={open}
      onClose={dismissActivePrompt}
      // Empty label so the AppBar header renders as an X-only bar
      // (matches the other modals — Modal.tsx renders AppBar whenever
      // `onClose` is provided). Never a floating cross.
      label=""
      maxWidth="md"
      closeOnOverlayClick
      contentClassName="rounded-3xl"
      desktopBodyClassName="rounded-3xl"
      // Strip the AppBar's built-in grey divider under the X on BOTH
      // desktop (Modal) and mobile (BottomSheet) — this modal is
      // header-less by design (Figma). Using the shared prop so the
      // selector applies on both surfaces.
      crossPlatformClassName="[&_[role=banner]]:!border-b-0"
    >
      {/* `-mt-4` negates ResponsiveModal's top py-4 so the image sits
          flush against the AppBar with zero whitespace above (per
          design). Bottom padding is preserved so the button still
          breathes above the modal's bottom edge; `md:pb-2` tops up the
          wrapper's 16px to the 24px desktop spec (mobile keeps 16).
          Item-to-item gap is 4. */}
      <div className="flex flex-col gap-4 -mt-4 md:pb-2">
        {/* Image spans the full content width so its left edge lines up
            with the title/body text (which sit against ResponsiveModal's
            px-6 padding — so the image's horizontal spacing is also 6). */}
        <ReviewMushroomUnit className="w-full h-auto" />
        <div className="flex flex-col gap-2">
          <h6 className="text-heading-6 font-semibold text-primary text-left">
            “Mushrooms grew from the floor!”
          </h6>
          <p className="text-body-2 text-primary text-left">
            A little research now could save you from disaster. See the real data in building
            reports.
          </p>
        </div>
        <Button variant="primary" size="md" fullWidth onClick={handleUnlock}>
          Unlock insights
        </Button>
      </div>
    </ResponsiveModal>
  );
}
